import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DynamicForm, SelectOption } from '../../../components/form-builder/form-builder.types';
import { FormBuilderComponent } from '../../../components/form-builder/form-builder.component';
import { MultiFormAccordionService } from '../../../components/multi-form-accordion/multi-form-accordion.service';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { PpmRepository } from '../../../store/ppm-state/ppm-state.repository';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { PpmService } from '../ppm.service';
import { ButtonModule } from 'primeng/button';
import { PpmMilestonesService } from '../ppm-milestones.service';
import { getPpmNewPlanFormObject } from './utils/ppm-create.form-object';
import { ppmPreDraftSchema } from '@/validations/ppm/ppm-predraft.validation';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { PpmWorkflowBlocksService } from '../ppm-workflow-blocks.service';
import { BlockBuilderComponent } from '../../../components/block-builder/block-builder.component';
import { ContractingAuthorityService } from '@/pages/settings/contracting-authority/contracting-authority.service';
import { StatusComponent } from '../../../components/shared/status/status.component';
import { InformativeStatusComponent } from '../../../components/shared/informative-status/informative-status.component';
import { CommonModule } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { InformativeStatus } from '@/components/shared/informative-status/informative-status.types';
import { getPpmNoContractingAuthorityInformativeStatus } from './utils/ppm-no-ac.informative';
import { TranslateService } from '@ngx-translate/core';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { ObjectPropertyLengthPipe } from '@/pipes/object-property-length.pipe';
import { ppmMilestonesStoreFactory } from '@/store/ppm-state/milestones-state/milestones.store';
import { AcYearAvailability } from '@/types';

@Component({
  selector: 'sm-ppm-new-plan',
  imports: [
    FormBuilderComponent,
    PageHeaderComponent,
    ButtonModule,
    BlockBuilderComponent,
    StatusComponent,
    InformativeStatusComponent,
    CommonModule,
    ObjectPropertyLengthPipe,
    SpinnerComponent,
  ],
  templateUrl: './new-plan.component.html',
  styleUrl: './new-plan.component.scss',
})
export class PpmNewPlanComponent implements OnInit, OnDestroy {
  // services
  ppmService = inject(PpmService);
  ppmMilestonesService = inject(PpmMilestonesService);
  ppmWorkflowBlocksService = inject(PpmWorkflowBlocksService);
  ppmRepository = inject(PpmRepository);
  multiFormAccordionService = inject(MultiFormAccordionService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);

  //breadcrumb
  breadcrumb = [
    {
      label: 'ppm.title',
      routerLink: ['/ppm-history'],
    },
    {
      label: 'ppm.variants.new',
      routerLink: ['ppm-new-plan'],
    },
  ];

  loading$ = new BehaviorSubject<boolean>(false);
  warning?: InformativeStatus;
  acOptions: SelectOption[] = [];
  acAvailability: AcYearAvailability[] = [];
  allowedYearsByAc = new Map<number, number[]>();
  yearDisabled$ = new BehaviorSubject<boolean>(true);
  currentAllowedYears: number[] = [];
  yearBounds: { minDate?: Date; maxDate?: Date } = {};
  formObject: DynamicForm = getPpmNewPlanFormObject({
    store: this.ppmRepository,
    acOptions: this.acOptions,
  });
  controlBlocks: DynamicBlocks = this.ppmWorkflowBlocksService.getPreDraftPpmBlocks({
    save: () => this.addPpm(),
    rest: () => this.reset(),
  });

  constructor() {
    this.multiFormAccordionService.formFactory = (id: string) => ppmMilestonesStoreFactory(id);
    this.multiFormAccordionService.init();
    this.buildForm();
  }

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy() {
    this.multiFormAccordionService.reset(0);
    this.ppmRepository.reset();
  }

  loadData() {
    this.loading$.next(true);
    this.ppmService.getAcWithNoPpm().subscribe({
      next: data => {
        this.acAvailability = data;
        this.allowedYearsByAc = new Map(data.map(ac => [ac.acId, ac.allowedYears || []]));
        this.acOptions = data.map(ac => {
          return { name: ac.acName, code: ac.acId };
        });
        if (this.acOptions.length === 1) {
          this.ppmRepository.setNested('dto', 'acId', this.acOptions[0].code as number);
          this.ppmRepository.set('acsSelected', this.acOptions[0]);
          this.applyAllowedYears(this.acOptions[0].code as number);
        } else {
          this.applyAllowedYears(undefined);
        }
        this.buildForm();
        this.ppmMilestonesService.initialize();
        this.loading$.next(false);
      },
      error: err => {
        this.warning = getPpmNoContractingAuthorityInformativeStatus({
          subtitle: err.error.message,
          goToPpmCurrentPlans: () => this.router.navigate(['/ppm-current-plans']),
          translate: this.translate,
        });
        this.loading$.next(false);
      },
    });
  }

  addPpm() {
    const year = this.ppmRepository.getNested('dto', 'annee');
    const milestoneValidation = this.ppmMilestonesService.validateAllMilestoneDtos('draft', year ? new Date(Number(year), 0, 1) : undefined);
    const payload = this.ppmRepository.getCreatePayload(this.ppmMilestonesService.getAllMilestoneDtos());
    const result = ppmPreDraftSchema.safeParse(payload.data);

    if (result.success && milestoneValidation) {
      this.loading$.next(true);
      this.ppmService.addPpmPerAc(payload.formData).subscribe({
        next: () => {
          this.multiFormAccordionService.reset(0);
          this.ppmRepository.reset();
          this.router.navigate(['/ppm-current-plans']);
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('ppm.message.success.added'),
          });
          this.loading$.next(false);
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || this.translate.instant('ppm.message.error.added'),
          });
          this.loading$.next(false);
        },
      });
    } else if (result.error) {
      this.ppmRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  reset() {
    this.ppmRepository.reset();
    this.multiFormAccordionService.reset();
    if (this.acOptions.length === 1) {
      this.ppmRepository.setNested('dto', 'acId', this.acOptions[0].code as number);
      this.ppmRepository.set('acsSelected', this.acOptions[0]);
      this.applyAllowedYears(this.acOptions[0].code as number);
    } else {
      this.applyAllowedYears(undefined);
    }
    this.buildForm();
  }

  private buildForm() {
    this.formObject = getPpmNewPlanFormObject({
      store: this.ppmRepository,
      acOptions: this.acOptions,
      yearConfig: {
        minDate: this.yearBounds.minDate,
        maxDate: this.yearBounds.maxDate,
        allowedYears: this.currentAllowedYears,
        isDisabled: this.yearDisabled$,
      },
      onAcChange: acId => this.handleAcChange(acId),
    });
  }

  private handleAcChange(acId?: number) {
    this.applyAllowedYears(acId);
    this.buildForm();
  }

  private applyAllowedYears(acId?: number) {
    if (!acId) {
      this.currentAllowedYears = [];
      this.yearBounds = {};
      this.yearDisabled$.next(true);
      this.ppmRepository.setNested('dto', 'annee', undefined);
      return;
    }
    const years = this.allowedYearsByAc.get(acId) || [];
    this.currentAllowedYears = years;
    if (!years.length) {
      this.yearDisabled$.next(true);
      this.ppmRepository.setNested('dto', 'annee', undefined);
      this.yearBounds = {};
      return;
    }
    this.yearDisabled$.next(false);
    if (years.length === 1) {
      this.ppmRepository.setNested('dto', 'annee', years[0].toString());
    } else {
      const currentYear = Number(this.ppmRepository.getNested('dto', 'annee'));
      if (!years.includes(currentYear)) {
        this.ppmRepository.setNested('dto', 'annee', years[0].toString());
      }
    }
    this.yearBounds = {
      minDate: new Date(Math.min(...years), 0, 1),
      maxDate: new Date(Math.max(...years), 11, 31),
    };
  }
}
